import { useState, useRef } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Typography, Switch, FormControlLabel, Stack
} from '@mui/material';
import { Add, Edit, Delete, Download, Upload } from '@mui/icons-material';
import { adminAPI } from '../../services/api';
import { downloadBlob, readFileAsText } from './csvUtils';

const CATEGORIES = ['breakfast', 'lunch', 'dinner', 'snack', 'drink', 'supplement', 'other'];
const EMPTY = { name: '', category: 'other', description: '', is_active: true };

export default function DietItemsTab({ rows, onRefresh, showSnackbar }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const uploadRef = useRef();

  const openAdd = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (row) => {
    setForm({
      name: row.name,
      category: row.category || 'other',
      description: row.description || '',
      is_active: row.is_active !== 0
    });
    setEditId(row.id);
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name) { showSnackbar('Name is required', 'error'); return; }
    setSaving(true);
    try {
      const payload = { ...form, is_active: form.is_active ? 1 : 0 };
      if (editId) {
        await adminAPI.updateDietItem(editId, payload);
        showSnackbar('Diet item updated');
      } else {
        await adminAPI.createDietItem(payload);
        showSnackbar('Diet item created');
      }
      setOpen(false);
      onRefresh();
    } catch (e) {
      showSnackbar(e.response?.data?.message || 'Save failed', 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await adminAPI.deleteDietItem(deleteId);
      showSnackbar('Diet item deleted');
      setDeleteId(null);
      onRefresh();
    } catch (e) {
      showSnackbar(e.response?.data?.message || 'Delete failed', 'error');
      setDeleteId(null);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const res = await adminAPI.downloadDietItemsCSV();
      downloadBlob(res.data, 'diet_items.csv');
    } catch { showSnackbar('CSV download failed', 'error'); }
  };

  const handleUploadCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    try {
      const csv = await readFileAsText(file);
      const res = await adminAPI.uploadDietItemsCSV(csv);
      const { inserted, updated, errors } = res.data.data;
      showSnackbar(
        `Inserted: ${inserted}, Updated: ${updated}${errors?.length ? `, Errors: ${errors.length}` : ''}`,
        errors?.length ? 'warning' : 'success'
      );
      onRefresh();
    } catch (e) { showSnackbar(e.response?.data?.message || 'CSV upload failed', 'error'); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">Diet Items ({rows.length})</Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<Download />} onClick={handleDownloadCSV}>Download CSV</Button>
          <Button size="small" variant="outlined" startIcon={<Upload />} onClick={() => uploadRef.current?.click()}>Upload CSV</Button>
          <input ref={uploadRef} type="file" accept=".csv" hidden onChange={handleUploadCSV} />
          <Button size="small" variant="contained" startIcon={<Add />} onClick={openAdd}>Add Item</Button>
        </Stack>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Category</strong></TableCell>
              <TableCell><strong>Description</strong></TableCell>
              <TableCell align="center"><strong>Active</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 4 }}>No diet items found</TableCell>
              </TableRow>
            )}
            {rows.map(row => (
              <TableRow key={row.id} hover sx={{ opacity: row.is_active ? 1 : 0.5 }}>
                <TableCell>{row.name}</TableCell>
                <TableCell><Chip label={row.category || 'other'} size="small" /></TableCell>
                <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.description || '—'}
                </TableCell>
                <TableCell align="center">
                  <Chip label={row.is_active ? 'Yes' : 'No'} size="small" color={row.is_active ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => openEdit(row)}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => setDeleteId(row.id)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Edit Diet Item' : 'Add Diet Item'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField label="Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} fullWidth />
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} label="Category">
              {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Description" value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))} multiline rows={2} fullWidth />
          <FormControlLabel
            control={<Switch checked={!!form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />}
            label="Item is active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs">
        <DialogTitle>Delete Diet Item?</DialogTitle>
        <DialogContent>Items referenced in active diet plans cannot be deleted.</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
